import { AppConfig } from './app-config.model'
import { CredentialType } from '../../login/login-config.model'
import { EmailVerificationMethod } from '../../pending-operation/pending-operation-config.model'

export const defaultAppConfig: AppConfig = {
  analytics: {
    metaPixel: {
      enabled: false,
      id: undefined,
    },
    googleGlobalSiteTag: {
      enabled: false,
      trackers: [],
      anonymizeData: true,
    },
    events: {
      signUpPageLoad: undefined,
      signUpSuccess: undefined,
    },
  },
  cookieBanner: {
    enabled: false,
    avacyCookieBanner: false,
    avacyConfigUrl: undefined,
  },
  core: {
    brandName: 'myCicero',
    clientName: 'pw-myCicero',
    cardLayoutLogo: {
      default: {
        height: 89,
        width: 157,
      },
    },
    documentTitle: 'myCicero Account',
    termsAndConditionsVisible: true,
    xhrRetryAttempts: 6,
    helpDeskEmail: 'servizioclienti@mycicero.it',
    helplineNumber: '+39 0282900734',
    monitoring: {
      enabled: false,
      sentry: {
        dsn: 'https://20ec3655e3fb4fef92258ecab5391dc8@o924572.ingest.sentry.io/4504694414376960',
        ignoreError: [],
      },
    },
    recaptcha: {
      siteKey: '6LeLtV8UAAAAAOxVTtKVBR7aSpIbmESlgPcAcKqP',
      testSiteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
    },
  },
  login: {
    singleSignOn: {
      clientName: 'myCiceroProfilazione',
    },
    paths: {
      _root: 'auth',
      clientLogin: 'client_login',
      ssoLogin: 'sso-login',
    },
    form: {
      fields: {
        email: {
          initialValue: '',
        },
        password: {
          initialValue: '',
        },
        mobileNumber: {
          initialValue: '',
        },
        mobileCountryCode: {
          initialValue: '+39',
        },
      },
    },
    credentialType: CredentialType.emailAddressAndMobilePhone,
    helpInfoMessageVisible: false,
    externalProvider: {
      enabled: false,
      paths: {
        _root: 'external-login',
        success: 'success',
        error: 'error',
      },
      providers: {},
    },
  },
  myCiceroWebApi: {
    urls: {
      _base: '/api-proxy',
      login: '/v2/Login/Login',
      ssoLogin: '/v2/Login/Sso',
      externalLogin: '/v2/Login/ExternalLogin',
      registerAccount: '/v2/SignUp/RegisterAccount',
      resetPassword: '/v2/Account/ResetPassword',
      changePassword: '/v2/Account/Pending/ChangePassword',
      validatePassword: '/v2/Signup/ValidatePassword',
      validateEmail: '/v2/SignUp/ValidateEmail',
      validateCell: '/v2/SignUp/ValidateCell',
      getAccountEncryptedData: '/Account/GetMaskedPersonalData',
      resendSms: '/v2/Account/Pending/ResendSms',
      confirmPinSms: '/v2/Account/Pending/ConfirmPinSms',
      changeRegistrationCellAndSendSms: '/v2/Account/Pending/ChangeRegistrationCellAndSendSms',
      changeMobile: '/Account/RequestCellChange',
      confirmEmail: '/v2/Account/Pending/ConfirmEmail',
      validateVatNumber: '/V2/SignUp/ValidateVatNumber',
      validateFiscalCode: '/V2/SignUp/ValidateFiscalCode',
      checkPhone: '/v2/SignUp/Thin/CheckPhone',
      resendPhoneOTP: '/v2/SignUp/Thin/ResendPhoneOTP',
      confirmPhoneOTP: '/v2/Signup/Thin/ConfirmPhoneOTP',
      checkEmail: '/v2/SignUp/Thin/CheckEmail',
      resendEmailOTP: '/v2/SignUp/Thin/ResendEmailOTP',
      confirmEmailOTP: '/v2/SignUp/Thin/ConfirmEmailOTP',
      completeRegistration: '/v2/SignUp/Thin/CompleteRegistration',
      sendOtpForAccountMatching: '/v2/SignUp/SendOtpForAccountMatching',
      confirmOtpForAccountMatching: '/v2/SignUp/ConfirmOtpForAccountMatching',
      searchCountry: '/v2/Configuration/SearchNazioni',
      searchCity: '/v2/Configuration/SearchComuni',
      getAddressPrefixList: '/v2/Account/GetToponym',
      getInvoicingSendingModalities: '/v2/Configuration/GetInvoiceSendingMode',
      getConsentsList: '/v2/Configuration/GetConsentsConfiguration',
      acceptConsents: '/v2/Account/Pending/AcceptConsents',
      insertPhone: '/v2/Account/Pending/InsertPhone',
      initialConfiguration: '/v2/Configuration/GetInitialConfiguration',
      generateHmacSignature: '/v2/Configuration/GenerateHmacSignature',
      getAuthorizeConsents: '/V2/Account/Pending/GetAuthorizeConsents',
      acceptAuthorizeConsents: '/V2/Account/Pending/AcceptAuthorizeConsents',
      startEmailCertification: '/v2/Account/Pending/StartEmailCertification',
      checkEmailCertification: '/v2/Account/Pending/CheckEmailCertification',
      confirmEmailCertification: '/v2/Account/ConfirmEmailCertification',
    },
  },
  signUp: {
    enabled: true,
    paths: {
      _root: 'sign-up',
    },
    form: {
      fields: {
        firstName: {
          required: true,
          initialValue: '',
          visible: true,
        },
        lastName: {
          required: true,
          initialValue: '',
          visible: true,
        },
        dateOfBirth: {
          required: false,
          initialValue: '',
          visible: false,
        },
        domicile: {
          required: false,
          initialValue: null,
          visible: false,
        },
        gender: {
          required: false,
          initialValue: 'Unknown',
          visible: false,
        },
        email: {
          required: true,
          initialValue: '',
          visible: true,
        },
        confirmEmail: {
          required: false,
          initialValue: '',
          visible: false,
        },
        mobileCountryCode: {
          required: true,
          initialValue: '+39',
          visible: true,
        },
        mobileNumber: {
          required: true,
          initialValue: '',
          visible: true,
        },
        password: {
          required: true,
          initialValue: '',
          visible: true,
        },
        passwordAgain: {
          required: true,
          initialValue: '',
          visible: true,
        },
        taxCode: {
          visible: false,
          required: false,
          initialValue: '',
          showInfoMessage: false,
        },
        consents: {
          required: true,
          initialValue: [],
          visible: true,
        },
        invoicingData: {
          required: false,
          visible: false,
          showInfoMessage: false,
          initialValue: {
            firstName: '',
            lastName: '',
            companyName: '',
            taxCode: '',
            vatNumber: '',
            pec: '',
            recipientCode: '',
            sendingMode: null,
            email: '',
            country: '',
            city: null,
            cityName: '',
            cityFreeTxt: '',
            region: '',
            address: '',
            addressPrefix: null,
            houseNumber: '',
            cap: '',
            electronicInvoicing: false,
            splitPayment: false,
          },
        },
        vatNumber: {
          required: false,
          initialValue: '',
          visible: false,
        },
        companyName: {
          required: false,
          initialValue: '',
          visible: false,
        },
      },
      domicile: {
        singleAddressField: false,
        showHouseNumber: false,
      },
      invoicingData: {
        addressPrefix: {
          enabled: false,
        },
        streetNumber: {
          enabled: true,
        },
        vatAndTaxCodeMutuallyExclusive: false,
      },
      companyToggleVisible: false,
    },
    eTollingInfoMessageVisible: false,
  },
  signUpFinalization: {
    enabled: true,
    paths: {
      _root: 'sign-up-finalization',
      selectMobile: 'select-mobile',
      unAssociatedNumberFailure: 'unassociated-failure',
      associatedNumberFailure: 'associated-failure',
      verifyMobile: 'verify-mobile',
      selectEmail: 'select-email',
      confirmEmailPin: 'confirm-email-pin',
      setPassword: 'set-password',
    },
  },
  pendingOperation: {
    paths: {
      _root: 'pending-operation',
      confirmMobile: 'confirm-mobile',
      changeMobile: 'change-mobile',
      emailSelection: 'email-selection',
      confirmEmail: 'confirm-email',
      externalConfirmEmail: 'external-confirm-email',
      needAcceptConsents: 'confirm-consents',
      changePassword: 'change-password',
      infoSharing: 'information-sharing',
      migration: {
        _root: 'migration',
        needInsertPhone: 'select-mobile',
        confirmPhone: 'confirm-mobile',
      },
      externalMatching: {
        _root: 'external-matching',
        confirmEmail: 'confirm-email',
        verifyEmail: 'verify-email',
        confirmMobile: 'confirm-mobile',
        verifyMobile: 'verify-mobile',
      },
      emailVerification: {
        _root: 'email-verification',
        oldLink: 'old-link',
        alreadyVerified: 'already-verified',
      },
    },
    emailVerificationMethod: EmailVerificationMethod.magicLinkVerification,
  },
  account: {
    paths: {
      _root: 'account',
      blocked: 'blocked',
      temporaryBlocked: 'temporary-blocked',
      unconfirmed: 'unconfirmed',
      active: 'active',
      inactive: 'inactive',
      closed: 'closed',
      closing: 'closing',
    },
  },
  resetPassword: {
    temporaryPassword: {
      enabled: true,
    },
    externalUrl: {
      enabled: false,
      url: null,
    },
    paths: {
      _root: 'reset-password',
    },
  },
  feedback: {
    paths: {
      _root: 'feedback',
      success: 'success',
      failure: 'failure',
      noCookies: 'no-cookies',
      networkError: 'network-error',
      genericError: 'generic-error',
      sessionExpired: 'session-expired',
    },
    failure: {
      networkImgBase64Src:
        'data:image/png;base64,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',
    },
  },
}
